import React, { useId, useMemo, useState } from 'react';

import { Button } from '@/design-system/button';
import { cn } from '@/design-system/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/design-system/popover';
import { SearchSelect } from '@/design-system/search-select';

import { warehouseApi } from '../api/warehouse.api';
import { useActivateOnFocus } from '../hooks/useActivateOnFocus';
import { useLoad } from '../hooks/useLoad';
import {
  DATE_PRESETS,
  type FilterField,
  type FilterValues,
  invalidRanges,
  type ListFilters,
} from '../hooks/useListFilters';
import { storageTypeOf } from '../hooks/useStorages';
import { STORAGE_TYPE_LABEL } from '../status';
import type { WarehouseStorage } from '../types';
import { useWarehouse } from '../WarehouseContext';
import { DateField } from './DateField';
import { FormField, TextInput, useFieldA11y } from './form';
import { usePickerA11y } from './pickerA11y';
import { CitySelect, ClientSelect, HubSelect, VendorSelect } from './pickers';
import { StorageSelect } from './StorageSelect';

/**
 * "Filter (N)" popover of the WMS lists, modelled on ShipmentFilterPanel:
 * the fields edit a local draft (seeded from the applied URL values when the
 * popover opens); Terapkan writes the draft, Reset clears everything — both in
 * one URL patch that also goes back to page 1. A date field is preset chips
 * OR a manual range (picking one clears the other) and counts as one filter.
 */

type Patch = Partial<Record<string, string>>;

const chipCls = (active: boolean) =>
  cn(
    'rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors',
    active
      ? 'border-accent text-accent'
      : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
  );

/**
 * Several controls under one FormField label (chips, a range, a chain): the
 * group is named by that label, each control inside names itself.
 */
const FieldGroup: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className, children }) => {
  const field = useFieldA11y();
  return (
    <div role="group" aria-labelledby={field?.labelId} className={className}>
      {children}
    </div>
  );
};

/* ------------------------------ storage chain ----------------------------- */

/** "Zona B (Quarantine)" — the name only, then its type. */
export const storageOptionLabel = (s: WarehouseStorage): string =>
  `${s.name} (${STORAGE_TYPE_LABEL[storageTypeOf(s)]})`;

/**
 * Location → Section → Bin, each a server-searched picker of its own level:
 * Sections are the chosen Location's direct children, Bins the chosen
 * Section's. A child waits (disabled, with its placeholder) for its parent, and
 * a new parent clears what was chosen below it.
 */
const StorageChain: React.FC<{
  value: FilterValues;
  onPatch: (p: Patch) => void;
}> = ({ value, onPatch }) => {
  const { hubID } = useWarehouse();
  const locationID = value.locationID ?? '';
  const sectionID = value.sectionID ?? '';
  const binID = value.binID ?? '';

  return (
    <FieldGroup className="flex flex-col gap-2">
      <StorageSelect
        aria-label="Location"
        hubID={hubID}
        status={null}
        category="LOCATION"
        labelOf={storageOptionLabel}
        value={locationID}
        placeholder={hubID ? 'Semua location' : 'Pilih hub dulu'}
        disabled={!hubID}
        onChange={(id) => {
          if (id !== locationID)
            onPatch({ locationID: id, sectionID: '', binID: '' });
        }}
        onClear={() => onPatch({ locationID: '', sectionID: '', binID: '' })}
      />
      <StorageSelect
        aria-label="Section"
        hubID={hubID}
        status={null}
        category="SECTION"
        parentID={locationID}
        labelOf={storageOptionLabel}
        value={sectionID}
        placeholder={locationID ? 'Semua section' : 'Pilih location dulu'}
        disabled={!locationID}
        onChange={(id) => {
          if (id !== sectionID) onPatch({ sectionID: id, binID: '' });
        }}
        onClear={() => onPatch({ sectionID: '', binID: '' })}
      />
      <StorageSelect
        aria-label="Bin"
        hubID={hubID}
        status={null}
        category="BIN"
        parentID={sectionID}
        labelOf={storageOptionLabel}
        value={binID}
        placeholder={sectionID ? 'Semua bin' : 'Pilih section dulu'}
        disabled={!sectionID}
        onChange={(id) => onPatch({ binID: id })}
        onClear={() => onPatch({ binID: '' })}
      />
    </FieldGroup>
  );
};

/* ------------------------------ upload batch ----------------------------- */

const UploadSelect: React.FC<{
  value: FilterValues;
  onPatch: (p: Patch) => void;
}> = ({ value, onPatch }) => {
  const { hubID } = useWarehouse();
  // Fetched once the user reaches the field, not when the panel opens.
  const { active, onFocus } = useActivateOnFocus();
  const load = useLoad(
    (signal) =>
      warehouseApi.listImports({ hubID, page: 1, limit: 100 }, { signal }),
    [hubID],
    active
  );
  const options = useMemo(
    () =>
      (load.data?.data ?? []).map((u) => ({
        value: u.id,
        label: `${u.code} · ${u.fileName}`,
      })),
    [load.data]
  );
  const ref = usePickerA11y(undefined, false);
  return (
    <div
      ref={ref}
      onFocus={onFocus}
      data-testid="filter-upload"
      className="min-w-0"
    >
      <SearchSelect
        options={options}
        value={value.uploadID ?? ''}
        selectedLabelFallback={value.uploadCode || value.uploadID || undefined}
        onChange={(v) => {
          const picked = (load.data?.data ?? []).find((u) => u.id === v);
          onPatch({
            uploadID: v || '',
            uploadCode: picked?.code ?? '',
          });
        }}
        loading={load.loading}
        placeholder="Semua batch"
        allowClear
        emptyMessage={load.error?.message ?? 'Batch upload tidak ditemukan'}
      />
    </div>
  );
};

/* --------------------------------- fields -------------------------------- */

const DateRange: React.FC<{
  field: Extract<FilterField, { kind: 'date' }>;
  value: FilterValues;
  onPatch: (p: Patch) => void;
}> = ({ field, value, onPatch }) => {
  const preset = `${field.key}Preset`;
  const from = `${field.key}From`;
  const to = `${field.key}To`;
  return (
    <FieldGroup>
      {field.presets && (
        <div className="mb-2 flex flex-wrap gap-1">
          {DATE_PRESETS[field.presets].map((p) => {
            const on = value[preset] === p.key;
            return (
              <button
                key={p.key}
                type="button"
                aria-pressed={on}
                className={chipCls(on)}
                // Toggle; a preset clears the manual range.
                onClick={() =>
                  onPatch({ [preset]: on ? '' : p.key, [from]: '', [to]: '' })
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2">
        <DateField
          size="sm"
          aria-label={`${field.label} dari`}
          placeholder="Dari"
          value={value[from] ?? ''}
          // A manual range clears the preset.
          onChange={(v) => onPatch({ [from]: v, [preset]: '' })}
          className="flex-1"
        />
        <span className="text-xs text-text-soft-400">s/d</span>
        <DateField
          size="sm"
          aria-label={`${field.label} sampai`}
          placeholder="Sampai"
          value={value[to] ?? ''}
          onChange={(v) => onPatch({ [to]: v, [preset]: '' })}
          className="flex-1"
        />
      </div>
    </FieldGroup>
  );
};

const FieldInput: React.FC<{
  field: FilterField;
  /** Id of the single control a text field labels (`<label htmlFor>`). */
  id: string;
  value: FilterValues;
  onPatch: (p: Patch) => void;
}> = ({ field, id, value, onPatch }) => {
  switch (field.kind) {
    case 'client':
      return (
        <ClientSelect
          value={
            value.clientID
              ? {
                  id: Number(value.clientID),
                  name: value.clientName || `Client #${value.clientID}`,
                }
              : null
          }
          onChange={(c) =>
            onPatch({
              clientID: c ? String(c.id) : '',
              clientName: c?.name ?? '',
            })
          }
          placeholder="Semua client"
          allowClear
        />
      );
    case 'vendor':
      return (
        <VendorSelect
          includeInactive
          value={
            value.vendorID
              ? {
                  id: value.vendorID,
                  code: '',
                  name: value.vendorName || value.vendorID,
                }
              : null
          }
          onChange={(v) =>
            onPatch({ vendorID: v?.id ?? '', vendorName: v?.name ?? '' })
          }
          placeholder="Semua vendor"
          allowClear
        />
      );
    case 'hub': {
      const id = Number(value[field.key]);
      return (
        <HubSelect
          value={value[field.key] && Number.isFinite(id) ? id : null}
          onChange={(hubID) =>
            onPatch({ [field.key]: hubID === null ? '' : String(hubID) })
          }
          allowAll
          aria-label={field.label}
        />
      );
    }
    case 'city':
      return (
        <CitySelect
          value={
            value.destinationCityID && value.destinationCityID !== 'none'
              ? {
                  id: Number(value.destinationCityID),
                  name:
                    value.destinationCityName ||
                    `Kota #${value.destinationCityID}`,
                }
              : null
          }
          rawValue={value.destinationCityID === 'none' ? 'none' : undefined}
          extraOptions={[{ value: 'none', label: 'Belum dikenali (kosong)' }]}
          onChange={(c, raw) =>
            onPatch({
              destinationCityID: raw ?? '',
              destinationCityName: c?.name ?? '',
            })
          }
          placeholder="Semua kota"
          allowClear
        />
      );
    case 'enum':
      return (
        <FieldGroup className="flex flex-wrap gap-1">
          {field.options.map((o) => {
            const on = value[field.key] === o.value;
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={on}
                data-testid={`filter-${field.key}-${o.value}`}
                className={chipCls(on)}
                onClick={() => onPatch({ [field.key]: on ? '' : o.value })}
              >
                {o.label}
              </button>
            );
          })}
        </FieldGroup>
      );
    case 'date':
      return <DateRange field={field} value={value} onPatch={onPatch} />;
    case 'text':
      return (
        <TextInput
          id={id}
          size="sm"
          value={value[field.key] ?? ''}
          onChange={(v) => onPatch({ [field.key]: v })}
          placeholder={field.placeholder}
        />
      );
    case 'upload':
      return <UploadSelect value={value} onPatch={onPatch} />;
    case 'storage':
      return <StorageChain value={value} onPatch={onPatch} />;
    default:
      return null;
  }
};

const fieldKey = (f: FilterField) => ('key' in f ? f.key : f.kind);

/** One labelled field of the panel; a bad date range shows under its field. */
const PanelField: React.FC<{
  field: FilterField;
  value: FilterValues;
  onPatch: (p: Patch) => void;
  badRange: boolean;
}> = ({ field, value, onPatch, badRange }) => {
  const id = useId();
  return (
    <FormField
      label={field.label}
      htmlFor={field.kind === 'text' ? id : undefined}
      error={
        badRange ? 'Tanggal awal tidak boleh setelah tanggal akhir.' : null
      }
    >
      <FieldInput field={field} id={id} value={value} onPatch={onPatch} />
    </FormField>
  );
};

/**
 * Popover box: never taller than the room Radix measured for it
 * (`--radix-popover-content-available-height`); header and footer keep their
 * size, the field list shrinks and scrolls.
 */
const FILTER_POPOVER_CLS =
  'flex w-[380px] max-h-(--radix-popover-content-available-height) flex-col p-0';

/* --------------------------------- panel --------------------------------- */

export const WarehouseFilterPanel: React.FC<{
  filters: ListFilters;
  /** Popover heading, e.g. "Filter purchase order". */
  title: string;
}> = ({ filters, title }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FilterValues>(filters.values);

  const onOpenChange = (next: boolean) => {
    // Seed the draft from what is applied every time the popover opens.
    if (next) setDraft(filters.values);
    setOpen(next);
  };
  const onPatch = (p: Patch) =>
    setDraft((d) => {
      const next: Record<string, string> = { ...d };
      Object.entries(p).forEach(([k, v]) => {
        next[k] = v ?? '';
      });
      return next;
    });

  // A manual range that ends before it starts never reaches the server.
  const badRanges = invalidRanges(filters.fields, draft);
  const apply = () => {
    if (badRanges.length) return;
    filters.write(draft);
    setOpen(false);
  };
  const reset = () => {
    setDraft(filters.empty);
    filters.write(filters.empty);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          tone="neutral"
          style="stroke"
          size="sm"
          data-testid="wms-filter"
          aria-label={
            filters.count ? `Filter (${filters.count} aktif)` : 'Filter'
          }
        >
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" aria-hidden />
          Filter
          {filters.count > 0 && (
            <span
              className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0"
              data-testid="wms-filter-count"
            >
              {filters.count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      {/*
        Capped to the space Radix measured below/above the trigger, with the
        fields as the only scroller: a long panel (Outbound request: 7 fields)
        under wrapped tabs must never push Reset / Terapkan off a 900px-high
        screen — the popper is position: fixed, so the page can't scroll to it.
      */}
      <PopoverContent
        align="end"
        collisionPadding={8}
        className={FILTER_POPOVER_CLS}
        aria-label={title}
        onInteractOutside={(e) => {
          // Clicks into a body-portaled SearchSelect menu must not close us.
          const t = e.target as HTMLElement | null;
          if (t?.closest?.('[data-searchable-dropdown-portal]'))
            e.preventDefault();
        }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            {title}
          </p>
          <Button
            tone="neutral"
            style="link"
            size="xs"
            className="text-[11px]"
            onClick={reset}
            data-testid="wms-filter-reset"
          >
            Reset
          </Button>
        </div>
        <div
          className="max-h-[60vh] min-h-0 space-y-4 overflow-y-auto px-4 py-3"
          data-testid="wms-filter-fields"
        >
          {filters.fields.map((f) => (
            <PanelField
              key={fieldKey(f)}
              field={f}
              value={draft}
              onPatch={onPatch}
              badRange={f.kind === 'date' && badRanges.includes(f.key)}
            />
          ))}
        </div>
        <div
          className="flex shrink-0 items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3"
          data-testid="wms-filter-footer"
        >
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => setOpen(false)}
          >
            Batal
          </Button>
          <Button
            size="sm"
            onClick={apply}
            disabled={badRanges.length > 0}
            data-testid="wms-filter-apply"
          >
            Terapkan
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
